import { z } from "zod";
import type { StandardCardMessage, CmdData } from "../sendmessage/Types";

// 定义物流卡片数据的Zod验证模式
export const CargoCardSchema = z.object({
  id: z.number(),
  order_no: z.string(),
  state: z.string(),
  origin_city: z.string(),
  destination_city: z.string(),
  cargo_type: z.string(),
  pos_x: z.number().optional(),
  pox_y: z.number().optional(),
  usrid: z.number().optional(),
  infos: z.array(z.object({ name: z.string(), value: z.any() })).optional(),
  cmd: z.any().optional(),
});

// 定义cmd_result数据的Zod验证模式
const cmd_result = z.object({
  text: z.string(),
});

// 定义cmd数据的Zod验证模式
export const CmdDataSchema = z.object({
  apiname: z.string(),
  order_id: z.string(),
  button: z.string(),
});

export const CargoCardListSchema = z.array(CargoCardSchema);

export const CmdResultSchema = z.object({
  cmd_result: cmd_result,
});

// 定义包含card和type字段的物流卡片外层结构
export const CargoCardWrapperSchema = z.object({
  card: z.string(),
  type: z.string(),
  data: CargoCardListSchema,
});

/**
 * 将物流卡片数据转换为标准卡片消息格式
 * @param cargoCards 物流卡片数据数组
 * @returns 标准卡片消息数组
 */
export function convertToStandardCardMessages(
  cargoCards: z.infer<typeof CargoCardListSchema>,
): StandardCardMessage[] {
  return cargoCards.map((card) => {
    const infos = card.infos || [];

    return {
      id: card.id.toString(),
      title: `物流订单 ${card.order_no}`,
      content: {
        origin: card.origin_city,
        destination: card.destination_city,
        status: card.state,
        type: card.cargo_type,
      },
      metadata: {
        orderNumber: card.order_no,
        position: {
          x: card.pos_x || 0,
          y: card.pox_y || 0,
        },
      },
      infos,
    };
  });
}

/**
 * 处理完整的消息转换流程：提取 -> 验证 -> 转换
 * @param messageText 原始消息文本
 * @returns 转换后的标准卡片消息数组，如果处理失败则返回null
 */
export function processMessageToStandardCards(
  messageText: string,
): StandardCardMessage[] | null {
  const cargoCards = parseCargoCardFromMessage(messageText);
  if (!cargoCards) {
    return null;
  }

  return convertToStandardCardMessages(cargoCards);
}

/**
 * 从消息文本中提取dada_card自定义标签数据
 * @param messageText 包含dada_card标签的消息文本
 * @returns 提取的JSON字符串，如果未找到则返回null
 */
export function extractDadaCardData(messageText: string): string | null {
  // 使用正则表达式匹配<dada_card>标签及其内容
  const regex = /<dada_card>(.*?)<\/dada_card>/s;

  const match = messageText.match(regex);

  if (!match || !match[1]) {
    return null;
  }

  try {
    const cardData = JSON.parse(match[1]);
    return JSON.stringify(cardData);
  } catch (error) {
    console.error("Failed to parse dada_card data:", error);
    return null;
  }
}

/**
 * 使用Zod验证物流卡片数据
 * @param jsonData 物流卡片数据的JSON字符串
 * @returns 验证后的卡片数据数组
 */
export function validateCargoCardData(
  jsonData: string,
): z.infer<typeof CargoCardListSchema> | null {
  try {
    const parsedData = JSON.parse(jsonData);

    // 尝试两种数据格式的验证：
    // 1. 检查是否是包含card和type字段的格式
    if (parsedData.card && parsedData.type && Array.isArray(parsedData.data)) {
      const validatedWrapper = CargoCardWrapperSchema.parse(parsedData);
      return validatedWrapper.data;
    }

    // 2. 检查是否是旧格式（直接有data字段）
    if (parsedData.data && Array.isArray(parsedData.data)) {
      const validatedData = CargoCardListSchema.parse(parsedData.data);
      return validatedData;
    }

    console.error("Invalid cargo card data structure");
    return null;
  } catch (error) {
    console.error("Cargo card data validation failed:", error);
    return null;
  }
}

/**
 * 解析消息文本中的物流卡片数据
 * @param messageText 包含dada_card标签的消息文本
 * @returns 验证后的卡片数据数组，如果解析失败则返回null
 */
export function parseCargoCardFromMessage(
  messageText: string,
): z.infer<typeof CargoCardListSchema> | null {
  const extractedData = extractDadaCardData(messageText);
  console.log("提取的 dada_card 数据:", extractedData);
  if (!extractedData) {
    return null;
  }

  try {
    const parsed = JSON.parse(extractedData);
    if (
      parsed &&
      typeof parsed === "object" &&
      parsed.card === "map" &&
      parsed.type === "map"
    ) {
      return null;
    }
  } catch {
    return null;
  }

  return validateCargoCardData(extractedData);
}

/**
 * 从消息文本中提取cmd_result自定义标签数据
 * @param messageText 包含cmd_result标签的消息文本
 * @returns 提取的纯文本内容，如果未找到则返回null
 */
export function extractCmdResultData(messageText: string): string | null {
  // 使用正则表达式匹配<cmd_result>标签及其内容
  const regex = /<cmd_result>(.*?)<\/cmd_result>/s;
  const match = messageText.match(regex);

  if (!match || !match[1]) {
    return null;
  }

  // 直接返回纯文本内容，不需要JSON解析
  return match[1].trim();
}

/**
 * 使用Zod验证cmd_result数据
 * @param jsonData cmd_result数据的JSON字符串
 * @returns 验证后的纯文本内容，如果验证失败则返回null
 */
export function validateCmdResultData(jsonData: string): string | null {
  try {
    const parsedData = JSON.parse(jsonData);
    const validatedData = CmdResultSchema.parse(parsedData);
    return validatedData.cmd_result.text;
  } catch (error) {
    console.error("Cmd result data validation failed:", error);
    return null;
  }
}

/**
 * 解析消息文本中的cmd_result数据
 * @param messageText 包含cmd_result标签的消息文本
 * @returns 验证后的纯文本内容，如果解析失败则返回null
 */
export function parseCmdResultFromMessage(messageText: string): string | null {
  return extractCmdResultData(messageText);
}

/**
 * 从消息文本中提取cmd数据
 * @param messageText 包含"cmd":内容的消息文本
 * @returns 提取的cmd对象，如果未找到则返回null
 */
export function extractCmdData(messageText: string): CmdData | null {
  // 使用正则表达式匹配"cmd": {...}格式
  const regex = /"cmd"\s*:\s*(\{[^}]+\})/;
  const match = messageText.match(regex);

  if (!match || !match[1]) {
    return null;
  }

  try {
    const cmdData = JSON.parse(match[1]);
    return validateCmdData(cmdData);
  } catch (error) {
    console.error("Failed to parse cmd data:", error);
    return null;
  }
}

/**
 * 使用Zod验证cmd数据
 * @param data cmd数据对象
 * @returns 验证后的cmd数据，如果验证失败则返回null
 */
export function validateCmdData(data: any): CmdData | null {
  try {
    const validatedData = CmdDataSchema.parse(data);
    return validatedData as CmdData;
  } catch (error) {
    console.error("Cmd data validation failed:", error);
    return null;
  }
}

// --- Plugin Data Handling ---

export const PluginDataSchema = z.object({
  type: z.string(),
  msg: z.string(),
  id: z.string(),
  state: z.number(),
});

export type PluginData = z.infer<typeof PluginDataSchema>;

/**
 * 从消息文本中提取plugin标签数据
 * @param messageText 包含plugin标签的消息文本
 * @returns 提取的插件数据数组
 */
export function extractPluginData(messageText: string): PluginData[] {
  const regex = /<plugin>(.*?)<\/plugin>/g;
  const plugins: PluginData[] = [];

  let match;
  while ((match = regex.exec(messageText)) !== null) {
    if (match[1]) {
      try {
        const pluginData = JSON.parse(match[1]);
        const validatedData = PluginDataSchema.parse(pluginData);
        plugins.push(validatedData);
      } catch (error) {
        console.error("Failed to parse plugin data:", error);
      }
    }
  }

  return plugins;
}

export const WeatherCardSchema = z.object({
  type: z.literal("weather"),
  data: z.object({
    nums: z.number().optional(),
    cityid: z.string().optional(),
    city: z.string(),
    update_time: z.string(),
    data: z.array(
      z.object({
        date: z.string(),
        wea: z.string(),
        wea_img: z.string(),
        tem_day: z.string(),
        tem_night: z.string(),
        win: z.string(),
        win_speed: z.string(),
      }),
    ),
  }),
});

// 定义天气卡片的标准输出格式
export type WeatherCardData = {
  city: string;
  updateTime: string;
  days: Array<{
    date: string;
    weather: string;
    icon: string;
    tempRange: string;
    wind: string;
    windSpeed: string;
  }>;
};

/**
 * 从消息文本中提取 JSON 对象
 * @returns 解析后的原始对象 (unknown)，如果未找到或解析失败则返回 null
 */
export function extractWeatherCardRaw(messageText: string): unknown | null {
  // 使用正则表达式匹配 <weather_card> 标签及其内容
  const regex = /<weather_card>(.*?)<\/weather_card>/s;
  const match = messageText.match(regex);

  if (!match || !match[1]) {
    return null;
  }

  try {
    return JSON.parse(match[1]);
  } catch (error) {
    console.error("Failed to parse weather_card JSON:", error);
    return null;
  }
}

/**
 * 直接验证对象，无需再次 JSON.parse
 */
export function validateWeatherCardData(
  data: unknown,
): z.infer<typeof WeatherCardSchema> | null {
  try {
    // Zod 可以直接验证 unknown 类型的对象
    return WeatherCardSchema.parse(data);
  } catch (error) {
    console.error("Weather card data validation failed:", error);
    return null;
  }
}

/**
 * 将验证后的数据转换为前端使用的标准格式
 */
export function convertToWeatherCardData(
  weatherCardData: z.infer<typeof WeatherCardSchema>,
): WeatherCardData {
  return {
    city: weatherCardData.data.city,
    updateTime: weatherCardData.data.update_time,
    days: weatherCardData.data.data.map((d) => ({
      date: d.date,
      weather: d.wea,
      icon: d.wea_img,
      tempRange: `${d.tem_night}° ~ ${d.tem_day}°`,
      wind: `${d.win}`,
      windSpeed: `${d.win_speed}`,
    })),
  };
}

/**
 * 主入口：解析消息文本中的天气卡片数据
 */
export function parseWeatherCard(messageText: string): WeatherCardData | null {
  // 1. 提取原始对象
  const rawData = extractWeatherCardRaw(messageText);
  if (!rawData) {
    return null;
  }

  // 2. 验证结构
  const validatedData = validateWeatherCardData(rawData);
  if (!validatedData) {
    return null;
  }

  // 3. 转换格式
  return convertToWeatherCardData(validatedData);
}

export const MapPointSchema = z.object({
  loc: z.object({
    x: z.number(),
    y: z.number(),
  }),
  info: z.string(),
});

export const MapCardSchema = z.object({
  card: z.literal("map"),
  type: z.literal("map"),
  data: z.array(MapPointSchema),
});

export type MapCardData = z.infer<typeof MapCardSchema>;

export function extractMapCardRaw(messageText: string): unknown | null {
  const tagRegex = /<map_card>(.*?)<\/map_card>/s;
  const tagMatch = messageText.match(tagRegex);

  if (tagMatch && tagMatch[1]) {
    try {
      return JSON.parse(tagMatch[1]);
    } catch (error) {
      console.error("Failed to parse map_card JSON:", error);
      return null;
    }
  }

  const dadaCardRaw = extractDadaCardData(messageText);
  if (dadaCardRaw) {
    try {
      const parsedDadaCard = JSON.parse(dadaCardRaw);
      if (
        parsedDadaCard &&
        typeof parsedDadaCard === "object" &&
        parsedDadaCard.card === "map" &&
        parsedDadaCard.type === "map"
      ) {
        return parsedDadaCard;
      }
    } catch {
      return null;
    }
  }

  try {
    const parsed = JSON.parse(messageText);
    if (parsed && typeof parsed === "object" && "card" in parsed) {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
}

export function parseMapCard(messageText: string): MapCardData | null {
  const rawData = extractMapCardRaw(messageText);
  if (!rawData) {
    return null;
  }

  try {
    return MapCardSchema.parse(rawData);
  } catch (error) {
    console.error("Map card data validation failed:", error);
    return null;
  }
}

const StreamIdDataSchema = z.object({
  id: z.number(),
});

// 解析stream_end标签
export type StreamIdData = z.infer<typeof StreamIdDataSchema>;

export function extractStreamId(messageText: string): StreamIdData | null {
  const regex = /<stream_end>\s*({.*?})\s*<\/stream_end>/;
  const match = messageText.match(regex);

  if (!match || !match[1]) {
    return null;
  }
  try {
    const jsonData = JSON.parse(match[1]);
    const result = StreamIdDataSchema.parse(jsonData);
    return result;
  } catch (error) {
    console.error("Failed to parse stream_end data:", error);
    return null;
  }
}

/**
 * 从消息文本中提取think标签数据（包括未闭合的情况）
 * @param messageText 包含think标签的消息文本
 * @returns 提取的思考内容文本，如果未找到则返回null
 */
export function extractThinkData(messageText: string): string | null {
  // 先尝试匹配完整的闭合标签
  const completeRegex = /<think>([\s\S]*?)<\/think>/;
  const completeMatch = messageText.match(completeRegex);

  if (completeMatch && completeMatch[1]) {
    return completeMatch[1].trim();
  }

  // 如果没有闭合标签，匹配开始标签后的所有内容
  const partialRegex = /<think>([\s\S]*?)$/;
  const partialMatch = messageText.match(partialRegex);

  if (partialMatch && partialMatch[1]) {
    // 返回部分内容，去除首尾空白
    return partialMatch[1].trim();
  }

  return null;
}

/**
 * 检测消息中是否包含think开始标签
 * @param messageText 消息文本
 * @returns 是否包含think开始标签
 */
export function hasThinkStart(messageText: string): boolean {
  return /<think>/i.test(messageText);
}

/**
 * 检测消息中是否包含think闭合标签
 * @param messageText 消息文本
 * @returns 是否包含think闭合标签
 */
export function hasThinkEnd(messageText: string): boolean {
  return /<\/think>/i.test(messageText);
}

export const WxUrlDataSchema = z.object({
  path: z.string(),
  name: z.string(),
  type: z.enum(["tabBar", "page"]),
  params: z
    .record(z.string(), z.union([z.string(), z.number()]))
    .optional()
    .default({}),
});

export type WxUrlData = z.infer<typeof WxUrlDataSchema>;

const WXURL_TAG_REGEX = /<wxurl>([\s\S]*?)<\/wxurl>/gi;

function parseWxUrlTag(jsonStr: string): WxUrlData | null {
  try {
    const validated = WxUrlDataSchema.safeParse(JSON.parse(jsonStr.trim()));
    return validated.success ? validated.data : null;
  } catch {
    return null;
  }
}

/**
 * 解析 wxurl 标签：生成 Markdown 内联链接文本、纯文本和按钮数据映射
 */
export function processWxUrlTags(messageText: string): {
  markdownText: string;
  plainText: string;
  wxUrlMap: Map<number, WxUrlData>;
} {
  const wxUrlMap = new Map<number, WxUrlData>();
  let index = 0;

  const markdownText = messageText.replace(WXURL_TAG_REGEX, (_, jsonStr) => {
    const data = parseWxUrlTag(jsonStr);
    if (!data) return "";
    const id = index++;
    wxUrlMap.set(id, data);
    return `[${data.name}](#wxurl-${id})`;
  });

  const plainText = messageText.replace(WXURL_TAG_REGEX, (_, jsonStr) => {
    const data = parseWxUrlTag(jsonStr);
    return data ? data.name : "";
  });

  return { markdownText, plainText, wxUrlMap };
}

export function buildMiniProgramUrl(
  path: string,
  params: Record<string, string | number> = {},
): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const entries = Object.entries(params);
  if (entries.length === 0) return normalizedPath;
  const query = entries
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("&");
  return `${normalizedPath}?${query}`;
}
